import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {NotificationChannelTypes} from "../../../../../../core/constants/enums";
import Api from "../../../../../../core/services/api/api";
import {Col, Row} from "reactstrap";
import {Box, CircularProgress, Divider, Fade, Paper, Tooltip} from "@mui/material";
import SearchInput from "../../../../../components/base/input/search_input";
import {Switch} from "../../../../../components/base/switch";
import {DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../containers/data-grid";
import {NotificationSubscriptionsDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useSearch from "../../../../../hooks/use-search";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import {ReactComponent as UserIcon} from "../../../../../../assets/images/notification-channels/user.svg";
import {ReactComponent as OperatorIcon} from "../../../../../../assets/images/notification-channels/operator.svg";
import {ReactComponent as SystemIcon} from "../../../../../../assets/images/notification-channels/system.svg";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../../../../components/base/chips/chips";
import OperatorAutocomplete from "../../../../../components/text-fields/autocomplete/operator-autocomplete";
import CustomerMemberAutocomplete from "../../../../../components/text-fields/autocomplete/customer-member-autocomplete";

const dataGridSaveConfig = ({
    name: 'UserNotificationSubscriptionsViewDataGrid',
    version: 1,
});

/**
 * Renders the icon associated with the given channel type.
 * @type {function(channel: *): JSX.Element}
 */
const renderChannelIcon = (channelTypeId) => {
    switch (channelTypeId) {
        case NotificationChannelTypes.user:
            return <>
                <UserIcon/>
            </>
        case NotificationChannelTypes.operator:
            return <>
                <OperatorIcon/>
            </>
        case NotificationChannelTypes.system:
            return <>
                <SystemIcon/>
            </>
        case NotificationChannelTypes.userAndSystem:
            return <>
                <UserIcon className={'mr-2'}/>
                <SystemIcon/>
            </>
        case NotificationChannelTypes.operatorAndSystem:
            return <>
                <OperatorIcon className={'mr-2'}/>
                <SystemIcon/>
            </>
        default:
            return <></>;
    }
};

const UserNotificationSubscriptionsView = () => {
    const [channels, setChannels] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState([]);
    const [keyword, setKeyword] = useState('');
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('user-notifications-subscriptions-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const [filteredChannels, filteredSubscriptions] = useMemo(() => {
        const keywords = keyword?.toLowerCase()?.trim();
        const filteredSubscriptions = subscriptions?.filter(s => s.channel?.description?.toLowerCase()?.includes(keywords)
            || s.user?.firstName?.toLowerCase()?.includes(keywords) || s.user?.lastName?.toLowerCase()?.includes(keywords) || s.user?.email?.toLowerCase()?.includes(keywords) || s.user?.phone?.toLowerCase()?.includes(keywords)
            || s.user?.customer?.name?.toLowerCase()?.includes(keywords)
            || s.operator?.firstName?.toLowerCase()?.includes(keywords) || s.operator?.lastName?.toLowerCase()?.includes(keywords) || s.operator?.email?.toLowerCase()?.includes(keywords) || s.operator?.phone?.toLowerCase()?.includes(keywords)
        );
        const filteredSubscriptionChannelIds = filteredSubscriptions?.map(s => s.channel?.id);
        return [
            channels?.filter(c => c.description?.toLowerCase()?.includes(keywords) || filteredSubscriptionChannelIds.includes(c.id)),
            filteredSubscriptions
        ];
    }, [channels, keyword, subscriptions])

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.userNotificationsSubscriptions);
        configureBehavioralTool((tags) => tags.userNotificationsSubscriptions);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    name: NotificationSubscriptionsDataGridColumnNames.icon,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    visibilityToggleable: false,
                    pinnedToggleable: false,
                    width: 0,
                },
                {
                    title: 'Notification Channel',
                    name: NotificationSubscriptionsDataGridColumnNames.channel,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: 350,
                },
                {
                    title: 'Subscribed To',
                    name: NotificationSubscriptionsDataGridColumnNames.entities,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: {type: DataGridColumnWidthTypes.flex, size: 1}
                },
                {
                    title: 'Subscribe To All',
                    name: NotificationSubscriptionsDataGridColumnNames.status,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    width: 110,
                    sortable: false,
                },
            ],
        [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Gets analytics report of the query in the url params from server
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const search = useCallback(async (args) => {
        const response = await Api.getOperatorSubscriptionChannelPageData({
            keyword: "",
            typeIds: [NotificationChannelTypes.system, NotificationChannelTypes.userAndSystem, NotificationChannelTypes.operatorAndSystem, NotificationChannelTypes.user, NotificationChannelTypes.operator],
        }, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        setChannels(response?.data?.availableChannels ?? []);
        setSubscriptions(response?.data?.operatorChannels ?? []);
    }, [isMounted])

    const initialSearchDataSettings = useMemo(() => ({
        filters: {},
    }), []);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Toggles the subscription status for a given channel.
     * @type {(function())}
     */
    const toggleChannelSubscription = useCallback(async (channel, subscribed, {
        user = null,
        operator = null,
    } = {}) => {
        let response;
        // If the subscription exists, do not create a duplicate
        if (subscribed) {
            if (user) {
                const exists = subscriptions?.find(s => s.channel?.id === channel?.id && s.user?.id === user?.id);
                if (exists)
                    return;
            } else if (operator) {
                const exists = subscriptions?.find(s => s.channel?.id === channel?.id && s.operator?.id === operator?.id);
                if (exists)
                    return;
            } else {
                const exists = subscriptions?.find(s => s.channel?.id === channel?.id && !s.user && !s.operator);
                if (exists)
                    return;
            }
        }
        setLoading((prevState) => ([...prevState, {channelId: channel?.id, userId: user?.id ?? null, operatorId: operator?.id ?? null}]));
        if (subscribed) {
            response = await Api.subscribeToNotificationChannel({channelId: channel?.id, operatorId: operator?.id, userId: user?.id,});
        } else {
            response = await Api.unSubscribeFromNotificationChannel({channelId: channel?.id, operatorId: operator?.id, userId: user?.id,});
        }
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setSubscriptions((prevState) => subscribed ? [...prevState, {channel: channel, user: user, operator: operator}] :
                prevState.filter(s => s.channel?.id !== channel?.id || (user && s.user?.id !== user?.id) || (operator && s.operator?.id !== operator?.id))
            );
        }
        setLoading((prevState) => prevState.filter(l => l.channelId !== channel?.id || (user && l.userId !== user?.id) || (operator && l.operatorId !== operator?.id)));
    }, [isMounted, subscriptions])

    /**
     * Creates the data entries for the table
     * @type {DataGridRow[]}
     */
    const dataGridRows = useMemo(() => {
        return filteredChannels?.map((channel) => {
            const allChanelSubscriptions = subscriptions?.filter(s => s.channel?.id === channel.id) ?? [];
            const channelSubscriptions = filteredSubscriptions?.filter(s => s.channel?.id === channel.id) ?? [];
            const hasAllSubscribed = !!allChanelSubscriptions?.find(e => !e.user && !e.operator);
            const channelLoading = !!loading?.find(l => l.channelId === channel.id && l.userId === null && l.operatorId === null);
            const useOperatorAutocomplete = channel.type?.id === NotificationChannelTypes.operator || channel.type?.id === NotificationChannelTypes.operatorAndSystem;
            const useUserAutocomplete = channel.type?.id === NotificationChannelTypes.user || channel.type?.id === NotificationChannelTypes.userAndSystem;
            return ({
                key: channel?.id,
                data: channel,
                cells: {
                    [NotificationSubscriptionsDataGridColumnNames.icon]:
                        <Tooltip
                            placement={'top'}
                            title={channel?.type?.title}
                            disableInteractive>
                            <div className={'d-flex align-items-center'}>
                                {renderChannelIcon(channel.type?.id)}
                            </div>
                        </Tooltip>,
                    [NotificationSubscriptionsDataGridColumnNames.channel]: channel?.description,
                    [NotificationSubscriptionsDataGridColumnNames.entities]:
                        <>
                            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, width: '100%'}}>
                                {
                                    channelSubscriptions?.map((subscription) => {
                                        const loadingRecord = !!loading?.find(l => l.channelId === channel.id && ((subscription?.user && l.userId === subscription?.user?.id) || (subscription?.operator && l.operatorId === subscription?.operator?.id)));
                                        return (
                                            <Chips
                                                key={`${subscription?.channel?.id}-${subscription?.user ? `user-${subscription?.user?.id}` : subscription?.operator ? `operator-${subscription?.operator?.id}` : 'all'}`}
                                                contentType={ChipsContentType.text}
                                                type={loadingRecord ? ChipsTypes.loading : ChipsTypes.remove}
                                                iconPosition={ChipsIconPosition.end}
                                                disabled={loadingRecord || channelLoading}
                                                onIconClick={() => toggleChannelSubscription(channel, false, {user: subscription?.user ?? null, operator: subscription?.operator ?? null,})}
                                                buttonProps={{size: 'small'}}
                                                content={
                                                    <SubscriptionChipContent
                                                        subscription={subscription}
                                                        channelTypeId={!!subscription?.user ? NotificationChannelTypes.user : !!subscription?.operator ? NotificationChannelTypes.operator : NotificationChannelTypes.system}
                                                    />
                                                }
                                            />
                                        );
                                    })
                                }
                                {
                                    useOperatorAutocomplete &&
                                    <OperatorAutocomplete
                                        label={'New Operator'}
                                        placeholder={'Search for operators...'}
                                        textFieldProps={{size: 'small'}}
                                        value={null}
                                        sx={{minWidth: 'min(200px, 100%)'}}
                                        disabled={channelLoading || hasAllSubscribed}
                                        onChange={(e, operator) => toggleChannelSubscription(channel, true, {operator: operator, user: null})}
                                    />
                                }
                                {
                                    useUserAutocomplete &&
                                    <CustomerMemberAutocomplete
                                        label={`New User`}
                                        placeholder={'Search for users...'}
                                        textFieldProps={{size: 'small'}}
                                        value={null}
                                        sx={{minWidth: 'min(500px, 100%)'}}
                                        disabled={channelLoading || hasAllSubscribed}
                                        onChange={(e, user) => toggleChannelSubscription(channel, true, {operator: null, user: user})}
                                    />
                                }
                            </Box>
                        </>,
                    [NotificationSubscriptionsDataGridColumnNames.status]:
                        <div className={'d-flex align-items-center justify-content-end'}>
                            <Switch
                                color={'primary'}
                                checked={hasAllSubscribed}
                                disabled={channelLoading}
                                onChange={(val) => toggleChannelSubscription(channel, val)}
                            />
                            <Fade in={channel?.loading} className={'ml-3'}>
                                <CircularProgress
                                    size={20}
                                    color={'secondary'}
                                />
                            </Fade>
                        </div>,
                },
            })
        })
    }, [filteredChannels, filteredSubscriptions, loading, subscriptions, toggleChannelSubscription]);


    return (
        <>
            <Row className='no-gutters w-100 justify-content-start align-items-center my-4'>
                <p className={'text-xl font-weight-semi-bold'}>
                    System Notification Subscriptions
                </p>
            </Row>
            <Paper className={`paper w-100 mb-3`}>
                <Row className='no-gutters align-items-center justify-content-between w-100'>
                    <Col xs={12} className='px-3'>
                        <SearchInput
                            autoSearch
                            immediateSearch
                            loading={searchData.loading}
                            initialValue={keyword}
                            search={(val) => setKeyword(val ?? "")}
                            label={'Search'}
                            placeholder={'Search over notification Channels and Subscriptions...'}
                        />
                    </Col>
                    <Divider className='divider'/>
                    <div className='w-100 my-2'/>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        rows={dataGridRows}
                        columns={dataGridColumns}
                        hideExporting
                        hideFooterActions
                        hideFooter
                        state={{
                            loading: {state: searchData.loading},
                            density: dataGridDensity,
                        }}
                    />
                </Row>
            </Paper>
        </>
    )
}

const SubscriptionChipContent = ({subscription, channelTypeId}) => {

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: '0.25rem 0.5rem'}}>
            {renderChannelIcon(channelTypeId)}
            {
                subscription?.user ?
                    `${subscription?.user?.firstName} ${subscription?.user?.lastName}` + (subscription?.user?.customer ? ` (${subscription?.user?.customer?.name})` : '')
                    :
                    subscription?.operator ?
                        `${subscription?.operator?.firstName} ${subscription?.operator?.lastName}`
                        : ""
            }
        </Box>
    );
}

export default UserNotificationSubscriptionsView;
